import { useState } from "react";
import { Dialog } from "../design/Dialog";
import { Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { Scrollbereich } from "../design/Scrollbereich";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";

/**
 * Mitgliedsreviere eines Jagdherrenkanals (oberflaeche.md §2.2, §7 „Kanal“; lesemodelle.md §4
 * `kanaele/{kanalId}/mitgliedsreviere`): `region` „Mitgliedsreviere“ mit der Gruppenüberschrift „Reviere“ (Symbol im
 * selben Element), je Revier ein `article` mit Name und Mitgliedsstatus. Das eigene Revier kann austreten
 * (`JagdherrenkanalAustreten`), das verwaltende Revier ein anderes entfernen (`RevierAusJagdherrenkanalEntfernen`);
 * beides wirkt erst nach „Bestätigen“ (kein Rückgängig, die Mitgliedschaft endet sofort). „Einladen“ öffnet den Dialog
 * „Einladen“ mit der Revierkennung, die der Jagdherr des anderen Reviers selbst weitergibt (K88 b, V-18: kein Verzeichnis
 * fremder Reviere).
 */
type Handlung = { art: "Austreten" | "Entfernen"; revierId: string; revier: string };

const BEFEHL: Record<Handlung["art"], string> = { Austreten: "JagdherrenkanalAustreten", Entfernen: "RevierAusJagdherrenkanalEntfernen" };

export function Mitgliedsreviere({ kanalId, eigenesRevier, verwaltend }: { kanalId: string; eigenesRevier: string | null; verwaltend: boolean }) {
  const reviere = useSammlung(`kanaele/${kanalId}/mitgliedsreviere`);
  const [handlung, setHandlung] = useState<Handlung | null>(null);
  const [abweisung, setAbweisung] = useState<string | null>(null);
  const [einladen, setEinladen] = useState(false);
  const [revierkennung, setRevierkennung] = useState("");
  const revierId = (z: Zeile) => (z["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? z._id;
  const liste = [...reviere.daten].sort((a, b) => Number(a["_reihenfolge"] ?? 0) - Number(b["_reihenfolge"] ?? 0));

  async function ausfuehren(h: Handlung) {
    setHandlung(null);
    const a = await befehl(BEFEHL[h.art], { Kanal: kanalId, Revier: h.revierId });
    setAbweisung(abweisungsgrund(a));
  }

  async function einladenSenden() {
    const kennung = revierkennung.trim();
    if (!kennung) return;
    setEinladen(false);
    setRevierkennung("");
    const a = await befehl("RevierInJagdherrenkanalEinladen", { Kanal: kanalId, Revier: kennung });
    setAbweisung(abweisungsgrund(a));
  }

  return (
    <section className="liste-bereich kanal__reviere" role="region" aria-label="Mitgliedsreviere">
      <div className="kanal__reviere-kopf">
        <h2 className="kopf platte gruppenkopf"><Symbol name="jagdherr" />Reviere</h2>
        <Knopf art="planung-zweit" symbol="einladung" onPress={() => { setAbweisung(null); setEinladen(true); }}>Einladen</Knopf>
      </div>
      <Scrollbereich>
        {liste.map((z) => {
          const id = revierId(z);
          const name = String(z["Revier"] ?? "—");
          const eigen = id === eigenesRevier;
          const titel = `mitgliedsrevier-${id}`;
          const knopf = eigen && z["Mitgliedsstatus"] === "angenommen"
            ? <Kachel symbol="schliessen" begriff="Austreten" art="kompakt" onPress={() => setHandlung({ art: "Austreten", revierId: id, revier: name })} />
            : !eigen && verwaltend
              ? <Kachel symbol="schliessen" begriff="Entfernen" art="kompakt" onPress={() => setHandlung({ art: "Entfernen", revierId: id, revier: name })} />
              : null;
          return (
            <article key={z._id} className="eintrag eintrag--zeile platte" aria-labelledby={titel}>
              <h3 className="eintrag__t" id={titel}>{name}</h3>
              <span className="eintrag__meta">{String(z["Mitgliedsstatus"] ?? "—")}</span>
              {knopf}
            </article>
          );
        })}
      </Scrollbereich>
      <Abweisung grund={abweisung} />
      <Dialog name="Einladen" symbol="einladung" offen={einladen} onSchliessen={() => setEinladen(false)}>
        <Textfeld label="Revierkennung" wert={revierkennung} onChange={setRevierkennung} mono autoFocus />
        <div className="knopfreihe">
          <Kachel symbol="einladung" begriff="Einladen" onPress={() => void einladenSenden()} />
          <Kachel symbol="schliessen" begriff="Abbrechen" onPress={() => setEinladen(false)} />
        </div>
      </Dialog>
      <Dialog name={handlung?.art ?? "Austreten"} symbol="schliessen" offen={handlung !== null} onSchliessen={() => setHandlung(null)} bestaetigung>
        <p className="erkl">{handlung ? `${handlung.revier} · sofort, kein Rückgängig` : null}</p>
        <div className="knopfreihe">
          <Kachel symbol="haken" begriff="Bestätigen" onPress={() => handlung && void ausfuehren(handlung)} />
          <Kachel symbol="schliessen" begriff="Abbrechen" onPress={() => setHandlung(null)} />
        </div>
      </Dialog>
    </section>
  );
}
